import React from 'react';
// components
import { Box } from '@mui/material';
import ToggleButton from '@mui/lab/ToggleButton';
import { makeStyles } from '@mui/styles';
import { InputLabelBase } from '../../inputs/InputLabelBase';
import { RegisteredToggleButtonGroup } from '../../Buttons/registered/v7/RegistedToggleButtonGroup';
import { withShowable } from '../../../console/_global/lib/withShowable';

const HiddenBox = withShowable(Box);
const LabelProps = {
  required: true,
  TypographyProps: {
    variant: 'body2',
    gutterBottom: true,
  },
};

function useShowableSecurityQuestions(showableQuestions: any) {
  return React.useMemo(() => {
    const SHOW_CLAIM_HISTORY = showableQuestions.includes('claimHistory');
    const SHOW_SECURITY_OFFICER =
      showableQuestions.includes('isSecurityOfficer');
    const SHOW_SECURITY_TRAINING =
      showableQuestions.includes('isSecurityTraining');
    const SHOW_USE_ENCRYPTION = showableQuestions.includes('useEncryption');
    const SHOW_USE_CLOUD_STORAGE =
      showableQuestions.includes('useCloudStorage');
    return {
      SHOW_CLAIM_HISTORY,
      SHOW_SECURITY_OFFICER,
      SHOW_SECURITY_TRAINING,
      SHOW_USE_ENCRYPTION,
      SHOW_USE_CLOUD_STORAGE,
    };
  }, [showableQuestions]);
}

export const P100RenewalSecurityQuestionsFields = ({
  disabled,
  showableQuestions,
}: any) => {
  const classes = useStyles();

  const toggleButtonProps = React.useMemo(
    () => ({
      disabled,
      className: classes.toggleButton,
      classes: {
        disabled: classes.toggleButtonDisabled,
      },
    }),
    // eslint-disable-next-line
    [disabled]
  );

  const {
    SHOW_CLAIM_HISTORY,
    SHOW_SECURITY_OFFICER,
    SHOW_SECURITY_TRAINING,
    SHOW_USE_ENCRYPTION,
    SHOW_USE_CLOUD_STORAGE,
  } = useShowableSecurityQuestions(showableQuestions);

  return (
    <div style={{ padding: '0rem 2.083rem' }}>
        <HiddenBox show={SHOW_CLAIM_HISTORY}>
          <InputLabelBase {...LabelProps} style={{ paddingTop: 0 }}>
            Have you ever had any cyber insurance claim(s)?
          </InputLabelBase>
          <RegisteredToggleButtonGroup name="claimHistory" exclusive>
            <ToggleButton value={0} {...toggleButtonProps}>
              <Box>Never</Box>
            </ToggleButton>
            <ToggleButton value={1} {...toggleButtonProps}>
              <Box>Within last 12 months</Box>
            </ToggleButton>
            <ToggleButton value={2} {...toggleButtonProps}>
              <Box>Within last 2 years</Box>
            </ToggleButton>
            <ToggleButton value={3} {...toggleButtonProps}>
              <Box>Within last 3 years</Box>
            </ToggleButton>
            <ToggleButton value={4} {...toggleButtonProps}>
              <Box>Within last 4 years</Box>
            </ToggleButton>
            <ToggleButton value={5} {...toggleButtonProps}>
              <Box>Within 5 years or more</Box>
            </ToggleButton>
          </RegisteredToggleButtonGroup>
        </HiddenBox>
        <HiddenBox show={SHOW_SECURITY_OFFICER}>
          <InputLabelBase {...LabelProps}>
            Does your organization have a Designated Information Security
            Contact?
          </InputLabelBase>
          <RegisteredToggleButtonGroup name="isSecurityOfficer" exclusive>
            <ToggleButton value {...toggleButtonProps}>
              <Box>Yes</Box>
            </ToggleButton>
            <ToggleButton value={false} {...toggleButtonProps}>
              <Box>No</Box>
            </ToggleButton>
          </RegisteredToggleButtonGroup>
        </HiddenBox>
        <HiddenBox show={SHOW_SECURITY_TRAINING}>
          <InputLabelBase {...LabelProps}>
            Does your organization provide mandatory information security
            training to all employees at least annually? If not, are you willing
            to implement it during the policy period?
          </InputLabelBase>
          <RegisteredToggleButtonGroup name="isSecurityTraining" exclusive>
            <ToggleButton value {...toggleButtonProps}>
              <Box>Yes</Box>
            </ToggleButton>
            <ToggleButton value={false} {...toggleButtonProps}>
              <Box>No</Box>
            </ToggleButton>
          </RegisteredToggleButtonGroup>
        </HiddenBox>
        <HiddenBox show={SHOW_USE_ENCRYPTION}>
          <InputLabelBase {...LabelProps}>
            Does your organization encrypt emails, mobile and computing devices
            containing sensitive information (e.g., PII, PHI, PCI) sent to
            external parties?
          </InputLabelBase>
          <RegisteredToggleButtonGroup name="useEncryption" exclusive>
            <ToggleButton value {...toggleButtonProps}>
              <Box>Yes</Box>
            </ToggleButton>
            <ToggleButton value={false} {...toggleButtonProps}>
              <Box>No</Box>
            </ToggleButton>
          </RegisteredToggleButtonGroup>
        </HiddenBox>
        <HiddenBox show={SHOW_USE_CLOUD_STORAGE}>
          <InputLabelBase {...LabelProps}>
            Does your organization have sensitive information stored on the
            cloud?
          </InputLabelBase>
          <RegisteredToggleButtonGroup name="useCloudStorage" exclusive>
            <ToggleButton value {...toggleButtonProps}>
              <Box>Yes</Box>
            </ToggleButton>
            <ToggleButton value={false} {...toggleButtonProps}>
              <Box>No</Box>
            </ToggleButton>
          </RegisteredToggleButtonGroup>
        </HiddenBox>
      </div>
  );
};

const useStyles = makeStyles(({ palette }) => {
  return {
    toggleButton: {
      padding: '0.5rem 4rem',
      marginBottom: '0.75rem',
    },
    toggleButtonDisabled: {
      color: `${palette.primary.contrastText} !important`,
    },
  };
});
